import { Component } from 'react'
import { LocalStorage, AuthService } from '../../Services'
import i18n from '../../i18n'
import { FaChevronRight } from 'react-icons/fa'
import { FiSun, FiMoon, FiLogOut, FiColumns, FiLifeBuoy, FiGlobe, FiSettings, FiSearch } from 'react-icons/fi'
import logo from '../../Assets/logo.svg'
import logoNatale from '../../Assets/logo_natale.png'
import styled from 'styled-components'
import { ThemeContext, LanguageContext } from '../../theme'
import CalendarWidget from './CalendarWidget'
import GlobalSearch from '../../Components/GlobalSearch'

const NavbarContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 50px;
  padding: 0 2rem;
  background: ${props => props.theme.colors.background.primary};
  border-bottom: 1px solid ${props => props.theme.colors.border.medium};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    padding: 0 1rem 0 3.5rem; /* spazio per il tasto sidebar (MobileTrigger) */
    height: 45px;
  }
`

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex: 1;
  min-width: 0;
`

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  @media (max-width: 768px) {
    display: none;
  }
`

const BreadcrumbItem = styled.button`
  background: none;
  border: none;
  color: ${props => props.$active ? props.theme.colors.semantic.primary : props.theme.colors.text.tertiary};
  font-size: 0.825rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: ${props => props.$active ? 'default' : 'pointer'};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  &:hover {
    background: ${props => props.$active ? 'transparent' : props.theme.colors.background.hover};
    color: ${props => props.theme.colors.semantic.primary};
  }

  &:focus {
    outline: none;
  }
`

const Separator = styled(FaChevronRight)`
  color: ${props => props.theme.colors.text.muted};
  font-size: 0.625rem;
  flex-shrink: 0;
`

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
  gap: 2px;
  cursor: pointer;

  img {
    height: 26px;
    width: auto;
    object-fit: contain;
  }

  @media (max-width: 1024px) {
    padding: 0 0.5rem;

    img {
      height: 20px;
    }
  }
`

const LogoText = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: 0.5px;
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  justify-content: flex-end;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`

const NavBaseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.semantic.primary};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;

  svg {
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  &:hover {
    background: ${props => props.theme.colors.semantic.primary + '1a'};
    color: ${props => props.theme.colors.semantic.primary};
  }

  &:active {
    background: ${props => props.theme.colors.background.disabled};
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;

    svg {
      font-size: 1rem;
    }
  }
`

const UserAvatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.theme.colors.semantic.primary};
  color: ${props => props.theme.colors.text.inverse};
  font-weight: 500;
  font-size: 0.75rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 0.85;
  }
`

const NavIconButton = styled(NavBaseButton)``

const AvatarMenuWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const AvatarDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${props => props.theme.colors.background.primary};
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: 10px;
  box-shadow: 0 8px 24px ${props => props.theme.colors.shadow.medium};
  min-width: 190px;
  z-index: 200;
  overflow: hidden;
`

const AvatarDropdownName = styled.div`
  padding: 0.7rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const AvatarDropdownDivider = styled.div`
  height: 1px;
  background: ${props => props.theme.colors.border.light};
  margin: 0.25rem 0;
`

const LanguageRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
`

const LanguageRowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.825rem;
  color: ${props => props.theme.colors.text.primary};

  svg {
    flex-shrink: 0;
    font-size: 1rem;
  }
`

const LanguageOptions = styled.div`
  display: flex;
  gap: 3px;
`

const LanguageOption = styled.button`
  padding: 3px 7px;
  border-radius: 5px;
  border: none;
  font-size: 0.7rem;
  font-weight: 600;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  background: ${props => props.$active ? props.theme.colors.semantic.primary : 'transparent'};
  color: ${props => props.$active ? props.theme.colors.text.inverse : props.theme.colors.text.tertiary};

  &:hover {
    background: ${props => props.$active ? props.theme.colors.semantic.primary : props.theme.colors.background.hover};
    color: ${props => props.$active ? props.theme.colors.text.inverse : props.theme.colors.text.primary};
  }
`

const AvatarDropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.625rem 1rem;
  background: none;
  border: none;
  font-size: 0.825rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: ${props => props.$danger ? props.theme.colors.semantic.danger : props.theme.colors.text.primary};
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;

  &:hover {
    background: ${props => props.theme.colors.background.hover};
  }

  svg {
    flex-shrink: 0;
    font-size: 1rem;
  }
`


const NavDivider = styled.div`
  width: 1px;
  height: 20px;
  background: ${props => props.theme.colors.border.medium};
  flex-shrink: 0;
  ${({ $hideOnMobile }) => $hideOnMobile && `
    @media (max-width: 768px) { display: none; }
  `}
`

const LANG_LABELS = { it: 'IT', en: 'EN', fr: 'FR' }

export default class NavbarObj extends Component {
  constructor (props) {
    super(props)
    this.state = {
      navbar: false,
      text: '',
      pageName: '',
      subPageName: '',
      numberPages: 0,
      user: {},
      showAvatarMenu: false
    }
    this.avatarMenuRef = null
  }

  async componentDidMount () {
    this.intervalCheck = setInterval(() => this.check(), 250)
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount () {
    clearInterval(this.intervalCheck)
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e) => {
    if (this.avatarMenuRef && !this.avatarMenuRef.contains(e.target)) {
      this.setState({ showAvatarMenu: false })
    }
  }

  check () {
    const user = LocalStorage.get('user')
    const location = window.location.pathname
    if (location !== '/' && location !== '/login' && location !== '/firstlogin' && location !== '/firstotp' && location !== '/resetpassword') {
      const page = window.location.pathname.split('/')[1]
      const subPage = window.location.pathname.split('/')[2]
      const pageName = page !== ''
        ? page[0].toUpperCase() + page.slice(1)
        : ''
      // Hide ObjectId-like subpages from breadcrumb (e.g. /board/69bd185f...)
      const isObjectId = subPage && /^[a-f0-9]{24}$/i.test(subPage)
      const subPageName = subPage && !isObjectId ? subPage[0].toUpperCase() + subPage.slice(1) : ''
      let numberPages = 0
      if (pageName === 'Home') {
        numberPages = 0
      } else if (pageName !== 'Home' && !subPageName) {
        numberPages = 1
      } else {
        numberPages = 2
      }
      this.setState({ user, pageName, subPageName, navbar: true, numberPages })
    } else {
      this.setState({ navbar: false })
    }
  }

  getUserInitials (user) {
    if (user.role === 'azienda' && user.azienda?.intestazione) {
      return user.azienda.intestazione.substring(0, 2).toUpperCase()
    } else if (user.nome && user.cognome) {
      return `${user.nome[0]}${user.cognome[0]}`.toUpperCase()
    }
    return 'U'
  }

  getUserDisplayName (user) {
    if (user.role === 'azienda' && user.azienda?.intestazione) {
      return user.azienda.intestazione
    } else if (user.nome && user.cognome) {
      return `${user.nome} ${user.cognome}`
    }
    return 'Utente'
  }

  getSeasonalLogo () {
    const now = new Date()
    const currentYear = now.getFullYear()
    const month = now.getMonth() + 1 // getMonth() returns 0-11
    const day = now.getDate()

    // Dal 6 novembre al 31 dicembre dell'anno corrente
    const startChristmas = new Date(currentYear, 10, 6) // 10 = novembre (0-indexed)

    if (now >= startChristmas || (month === 1 && day <= 6)) {
      return logoNatale
    }

    return logo
  }

  async logout () {
    await AuthService.logout(this.props.navigate)
  }

  renderBreadcrumb () {
    const { pageName, subPageName, numberPages } = this.state

    if (numberPages !== 2) return null

    return (
      <>
        <BreadcrumbItem
          key='page'
          onClick={() => this.props.navigate(`/${pageName.toLowerCase()}`)}
        >
          {pageName}
        </BreadcrumbItem>
        <Separator key='sep' />
        <BreadcrumbItem key='subpage' $active>
          {subPageName}
        </BreadcrumbItem>
      </>
    )
  }

  render () {
    const { navbar, user } = this.state
    if (!navbar) return null
    if (!user) return null

    const initials = this.getUserInitials(user)
    const displayName = this.getUserDisplayName(user)

    return (
      <NavbarContainer>
        <LeftSection>
          <LogoContainer onClick={() => this.props.navigate('/home')}>
            {(() => {
              const currentLogo = this.getSeasonalLogo()
              return (
                <>
                  <img src={currentLogo} alt='Logo' />
                  {currentLogo === logo && <LogoText>EMS</LogoText>}
                </>
              )
            })()}
          </LogoContainer>

          <NavDivider $hideOnMobile />

          <BreadcrumbContainer>
            {this.renderBreadcrumb()}
          </BreadcrumbContainer>
        </LeftSection>

        <RightSection>
          <NavIconButton
            onClick={() => this.globalSearch && this.globalSearch.open()}
            title={i18n.t('search', { ns: 'navbar' })}
          >
            <FiSearch />
          </NavIconButton>

          <NavIconButton
            onClick={() => this.props.navigate('/board')}
            title={i18n.t('board', { ns: 'navbar' })}
          >
            <FiColumns />
          </NavIconButton>

          <CalendarWidget navigate={this.props.navigate} />
          <GlobalSearch ref={ref => { this.globalSearch = ref }} navigate={this.props.navigate} />

          <NavDivider />

          <AvatarMenuWrapper ref={ref => { this.avatarMenuRef = ref }}>
            <UserAvatar
              title={displayName}
              onClick={() => this.setState(prev => ({ showAvatarMenu: !prev.showAvatarMenu }))}
            >
              {initials}
            </UserAvatar>
            {this.state.showAvatarMenu && (
              <AvatarDropdown>
                <AvatarDropdownName>{displayName}</AvatarDropdownName>

                <AvatarDropdownItem onClick={() => { this.setState({ showAvatarMenu: false }); this.props.navigate('/settaggi') }}>
                  <FiSettings /> {i18n.t('settings', { ns: 'navbar' })}
                </AvatarDropdownItem>

                <AvatarDropdownItem onClick={() => { this.setState({ showAvatarMenu: false }); this.props.navigate('/ticket') }}>
                  <FiLifeBuoy /> {i18n.t('assistance', { ns: 'navbar' })}
                </AvatarDropdownItem>

                <ThemeContext.Consumer>
                  {({ isDark, toggleTheme }) => (
                    <AvatarDropdownItem onClick={toggleTheme}>
                      {isDark ? <FiSun /> : <FiMoon />} {i18n.t(isDark ? 'themeLight' : 'themeDark', { ns: 'navbar' })}
                    </AvatarDropdownItem>
                  )}
                </ThemeContext.Consumer>

                <LanguageContext.Consumer>
                  {({ language, setLanguage, supportedLanguages }) => (
                    <LanguageRow>
                      <LanguageRowLabel><FiGlobe /> {i18n.t('language', { ns: 'navbar' })}</LanguageRowLabel>
                      <LanguageOptions>
                        {supportedLanguages.map(lang => (
                          <LanguageOption
                            key={lang}
                            $active={lang === language}
                            onClick={() => setLanguage(lang)}
                          >
                            {LANG_LABELS[lang] || lang.toUpperCase()}
                          </LanguageOption>
                        ))}
                      </LanguageOptions>
                    </LanguageRow>
                  )}
                </LanguageContext.Consumer>

                <AvatarDropdownDivider />

                <AvatarDropdownItem $danger onClick={() => { this.setState({ showAvatarMenu: false }); this.logout() }}>
                  <FiLogOut /> {i18n.t('logout', { ns: 'navbar' })}
                </AvatarDropdownItem>
              </AvatarDropdown>
            )}
          </AvatarMenuWrapper>
        </RightSection>
      </NavbarContainer>
    )
  }
}
